import React, { useMemo, useState } from "react";
import { submitLead } from "../lib/api";
import { SURFACES, STACK_OPTIONS, REVENUE_BANDS, AUTONOMY_LEVELS } from "../lib/directions";

/**
 * The single conversion surface, shared by all five directions but fully skinned.
 * Theme is delivered as CSS variables so each direction keeps its own art direction.
 *
 * theme = { bg, panel, field, ink, muted, accent, accentInk, border, radius,
 *           fontDisplay, fontLabel, fontBody, labelStyle }
 */
const STEPS = ["The bottleneck", "The boundaries", "You"];

function ChipBase({ active, children, onClick, testid, radius }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{ borderRadius: radius }}
      className={`focus-ring border px-3 py-2 text-left text-[12.5px] transition-all ${
        active
          ? "border-[var(--wm-accent)] bg-[var(--wm-accent)] text-[var(--wm-accent-ink)]"
          : "border-[var(--wm-border)] bg-[var(--wm-field)] text-[var(--wm-ink)] hover:border-[var(--wm-accent)]"
      }`}
    >
      {children}
    </button>
  );
}

function ErrBase({ k, errors }) {
  if (!errors[k]) return null;
  return (
    <p className="mt-1.5 flex items-center gap-1.5 text-[11.5px] text-[#ff8a80]" data-testid={`wm-error-${k}`}>
      <span aria-hidden>!</span> {errors[k]}
    </p>
  );
}


export default function WorkflowMapper({ theme, directionId, directionName, simulationSummary = {}, eyebrow, title, blurb }) {
  const [step, setStep] = useState(0);
  const [state, setState] = useState({
    surface: "",
    bottleneck: "",
    frequency: "weekly",
    hours_per_week: "",
    blended_rate: "",
    autonomy_comfort: "draft",
    stack: [],
    name: "",
    email: "",
    brand: "",
    brand_url: "",
    revenue_band: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [result, setResult] = useState(null);
  const [serverError, setServerError] = useState("");

  const set = (k) => (v) => setState((s) => ({ ...s, [k]: v }));

  const annual = useMemo(() => {
    const h = parseFloat(state.hours_per_week);
    const r = parseFloat(state.blended_rate);
    if (!h || !r) return null;
    return Math.round(h * r * 52);
  }, [state.hours_per_week, state.blended_rate]);

  const validate = (s) => {
    const e = {};
    if (s === 0) {
      if (!state.bottleneck.trim() || state.bottleneck.trim().length < 8)
        e.bottleneck = "Describe the recurring work in a sentence or two.";
      if (!state.surface) e.surface = "Pick the part of the business it lives in.";
    }
    if (s === 2) {
      if (!state.name.trim()) e.name = "Required.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) e.email = "A working email, please.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate(step)) setStep((s) => Math.min(STEPS.length - 1, s + 1)); };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async (ev) => {
    ev?.preventDefault?.();
    if (!validate(2)) return;
    setStatus("sending");
    setServerError("");
    try {
      const payload = {
        ...state,
        hours_per_week: state.hours_per_week ? parseFloat(state.hours_per_week) : null,
        blended_rate: state.blended_rate ? parseFloat(state.blended_rate) : null,
        direction_id: directionId,
        direction_name: directionName,
        simulation_summary: simulationSummary,
      };
      const data = await submitLead(payload);
      setResult(data);
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setServerError(
        err?.response?.data?.detail
          ? typeof err.response.data.detail === "string"
            ? err.response.data.detail
            : "Please check the highlighted fields."
          : "Could not reach the server. Try again in a moment."
      );
    }
  };

  const vars = {
    "--wm-bg": theme.bg,
    "--wm-panel": theme.panel,
    "--wm-field": theme.field,
    "--wm-ink": theme.ink,
    "--wm-muted": theme.muted,
    "--wm-accent": theme.accent,
    "--wm-accent-ink": theme.accentInk,
    "--wm-border": theme.border,
  };

  const radius = theme.radius ?? "12px";
  const inputCls =
    "focus-ring w-full bg-[var(--wm-field)] px-3.5 py-3 text-[14px] text-[var(--wm-ink)] placeholder:text-[var(--wm-muted)] border border-[var(--wm-border)] transition-colors focus:border-[var(--wm-accent)]";
  const labelCls = `${theme.fontLabel || "f-mono"} mb-2 block text-[10px] uppercase tracking-[0.16em] text-[var(--wm-muted)]`;


  if (status === "done") {
    const emailHandoff = result?.delivery === "email";
    return (
      <div style={{ ...vars, borderRadius: radius }} className="border border-[var(--wm-border)] bg-[var(--wm-panel)] p-6 sm:p-9" data-testid="workflow-mapper-success">
        <div className={`${theme.fontLabel || "f-mono"} mb-4 text-[10px] uppercase tracking-[0.18em] text-[var(--wm-accent)]`}>
          {emailHandoff ? "Workflow prepared · email handoff" : `Workflow mapped · reference ${result?.id?.slice(0, 8)}`}
        </div>
        <h3 className={`${theme.fontDisplay} mb-4 text-[26px] leading-[1.15] text-[var(--wm-ink)] sm:text-[34px]`}>
          {emailHandoff
            ? `${state.name.split(" ")[0]}, your workflow map is ready to send.`
            : `${state.name.split(" ")[0]}, this is where an operator would start.`}
        </h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <Summary label="Starting surface" value={SURFACES.find((s) => s.id === state.surface)?.label || "not set"} theme={theme} />
          <Summary label="Opening autonomy" value={AUTONOMY_LEVELS.find((a) => a.id === state.autonomy_comfort)?.label} theme={theme} />
          <Summary
            label="Annual cost of the drag"
            value={result?.annual_cost_estimate ? `$${Number(result.annual_cost_estimate).toLocaleString()}` : "To be priced together"}
            theme={theme}
          />
        </div>
        <div className="mt-6 space-y-2.5 border-t border-[var(--wm-border)] pt-6 text-[13.5px] leading-[1.6] text-[var(--wm-muted)]">
          <p>{emailHandoff
            ? "Your email app should have opened with the workflow details. Send that message to deliver the map to Austin. Nothing was silently stored by this website."
            : "Austin reads this himself. If the workflow is a fit, you get a written map back: the signals it needs, the permissions it needs, the gate that stays human, and the evidence it has to produce before it earns another inch of autonomy."}
          </p>
          <p>If it is not a fit, he will tell you that instead, and why.</p>
        </div>
        <blockquote className="mt-6 border-l-2 border-[var(--wm-accent)] pl-4 text-[13px] italic leading-[1.6] text-[var(--wm-muted)]">
          “{state.bottleneck.trim()}”
        </blockquote>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      style={{ ...vars, borderRadius: radius }}
      className="border border-[var(--wm-border)] bg-[var(--wm-panel)] p-5 sm:p-8"
      data-testid="workflow-mapper"
    >
      {eyebrow && (
        <div className={`${theme.fontLabel || "f-mono"} mb-3 text-[10px] uppercase tracking-[0.18em] text-[var(--wm-accent)]`}>{eyebrow}</div>
      )}
      {title && <h3 className={`${theme.fontDisplay} mb-2 text-[26px] leading-[1.1] text-[var(--wm-ink)] sm:text-[34px]`}>{title}</h3>}
      {blurb && <p className="mb-7 max-w-[60ch] text-[13.5px] leading-[1.65] text-[var(--wm-muted)]">{blurb}</p>}

      {/* step rail */}
      <div className="mb-7 flex items-center gap-2" data-testid="workflow-mapper-steps">
        {STEPS.map((s, i) => (
          <div key={s} className="flex flex-1 items-center gap-2">
            <div className="flex-1">
              <div
                className="h-[2px] w-full transition-colors duration-300"
                style={{ background: i <= step ? "var(--wm-accent)" : "var(--wm-border)" }}
              />
              <div
                className={`${theme.fontLabel || "f-mono"} mt-2 text-[9.5px] uppercase tracking-[0.14em]`}
                style={{ color: i <= step ? "var(--wm-ink)" : "var(--wm-muted)" }}
              >
                {i + 1}. {s}
              </div>
            </div>
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="space-y-6">
          <div>
            <label className={labelCls}>Where does it live?</label>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {SURFACES.map((s) => (
                <ChipBase radius={radius} key={s.id} active={state.surface === s.id} onClick={() => set("surface")(s.id)} testid={`wm-surface-${s.id}`}>
                  {s.label}
                </ChipBase>
              ))}
            </div>
            <ErrBase k="surface" errors={errors} />
          </div>

          <div>
            <label className={labelCls} htmlFor="wm-bottleneck">
              The recurring work that costs you the most
            </label>
            <textarea
              id="wm-bottleneck"
              data-testid="wm-bottleneck-input"
              rows={4}
              value={state.bottleneck}
              onChange={(e) => set("bottleneck")(e.target.value)}
              placeholder="e.g. Every Monday someone rebuilds the same performance rollup, then nobody has time to act on what it says."
              style={{ borderRadius: radius }}
              className={inputCls}
            />
            <ErrBase k="bottleneck" errors={errors} />
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <div>
              <label className={labelCls}>How often</label>
              <div className="flex gap-2">
                {["daily", "weekly", "monthly"].map((f) => (
                  <ChipBase radius={radius} key={f} active={state.frequency === f} onClick={() => set("frequency")(f)} testid={`wm-frequency-${f}`}>
                    {f}
                  </ChipBase>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls} htmlFor="wm-hours">Hours a week it eats</label>
              <input
                id="wm-hours"
                data-testid="wm-hours-input"
                inputMode="decimal"
                value={state.hours_per_week}
                onChange={(e) => set("hours_per_week")(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="6"
                style={{ borderRadius: radius }}
                className={`${inputCls} tnum`}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="wm-rate">Blended hourly cost</label>
              <input
                id="wm-rate"
                data-testid="wm-rate-input"
                inputMode="decimal"
                value={state.blended_rate}
                onChange={(e) => set("blended_rate")(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="85"
                style={{ borderRadius: radius }}
                className={`${inputCls} tnum`}
              />
            </div>
          </div>

          {annual !== null && (
            <div
              className="flex items-baseline justify-between border-t border-[var(--wm-border)] pt-4"
              data-testid="wm-annual-cost"
            >
              <span className={`${theme.fontLabel || "f-mono"} text-[10px] uppercase tracking-[0.16em] text-[var(--wm-muted)]`}>
                Annual cost of this one workflow
              </span>
              <span className={`${theme.fontDisplay} tnum text-[24px] text-[var(--wm-accent)]`}>${annual.toLocaleString()}</span>
            </div>
          )}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-6">
          <div>
            <label className={labelCls}>How much rope would you give it on day one?</label>
            <div className="space-y-2">
              {AUTONOMY_LEVELS.map((a, i) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => set("autonomy_comfort")(a.id)}
                  data-testid={`wm-autonomy-${a.id}`}
                  style={{ borderRadius: radius }}
                  className={`focus-ring flex w-full items-start gap-3 border p-3 text-left transition-all ${
                    state.autonomy_comfort === a.id
                      ? "border-[var(--wm-accent)] bg-[var(--wm-field)]"
                      : "border-[var(--wm-border)] bg-transparent hover:border-[var(--wm-muted)]"
                  }`}
                >
                  <span
                    className={`${theme.fontLabel || "f-mono"} mt-0.5 text-[10px]`}
                    style={{ color: state.autonomy_comfort === a.id ? "var(--wm-accent)" : "var(--wm-muted)" }}
                  >
                    L{i + 1}
                  </span>
                  <span>
                    <span className="block text-[13.5px] font-medium text-[var(--wm-ink)]">{a.label}</span>
                    <span className="block text-[12px] text-[var(--wm-muted)]">{a.blurb}</span>
                  </span>
                  {state.autonomy_comfort === a.id && <span className="ml-auto text-[var(--wm-accent)]">✓</span>}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls}>What is it allowed to see and touch?</label>
            <div className="flex flex-wrap gap-2">
              {STACK_OPTIONS.map((s) => (
                <ChipBase
                  radius={radius}
                  key={s}
                  active={state.stack.includes(s)}
                  testid={`wm-stack-${s.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() =>
                    set("stack")(state.stack.includes(s) ? state.stack.filter((x) => x !== s) : [...state.stack, s])
                  }
                >
                  {s}
                </ChipBase>
              ))}
            </div>
            <p className="mt-3 text-[11.5px] leading-relaxed text-[var(--wm-muted)]">
              Credentials never become searchable knowledge. Access is scoped per workflow and revocable in one move.
            </p>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className={labelCls} htmlFor="wm-name">Name</label>
              <input id="wm-name" data-testid="wm-name-input" value={state.name} onChange={(e) => set("name")(e.target.value)} placeholder="Dana Reyes" style={{ borderRadius: radius }} className={inputCls} />
              <ErrBase k="name" errors={errors} />
            </div>
            <div>
              <label className={labelCls} htmlFor="wm-email">Work email</label>
              <input id="wm-email" data-testid="wm-email-input" value={state.email} onChange={(e) => set("email")(e.target.value)} placeholder="dana@brand.com" style={{ borderRadius: radius }} className={inputCls} />
              <ErrBase k="email" errors={errors} />
            </div>
            <div>
              <label className={labelCls} htmlFor="wm-brand">Brand</label>
              <input id="wm-brand" data-testid="wm-brand-input" value={state.brand} onChange={(e) => set("brand")(e.target.value)} placeholder="Klunk" style={{ borderRadius: radius }} className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="wm-url">Store URL</label>
              <input id="wm-url" data-testid="wm-url-input" value={state.brand_url} onChange={(e) => set("brand_url")(e.target.value)} placeholder="klunk.com" style={{ borderRadius: radius }} className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Annual revenue</label>
            <div className="flex flex-wrap gap-2">
              {REVENUE_BANDS.map((b) => (
                <ChipBase radius={radius} key={b} active={state.revenue_band === b} onClick={() => set("revenue_band")(b)} testid={`wm-revenue-${b.replace(/[^a-z0-9]/gi, "")}`}>
                  {b}
                </ChipBase>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls} htmlFor="wm-notes">Anything else worth knowing</label>
            <textarea id="wm-notes" data-testid="wm-notes-input" rows={3} value={state.notes} onChange={(e) => set("notes")(e.target.value)} placeholder="Optional." style={{ borderRadius: radius }} className={inputCls} />
          </div>
        </div>
      )}

      {status === "error" && (
        <p className="mt-5 border border-[#ff8a80]/40 bg-[#ff8a80]/10 px-3.5 py-2.5 text-[12.5px] text-[#ffb3aa]" data-testid="wm-server-error">
          {serverError}
        </p>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-[var(--wm-border)] pt-6">
        {step > 0 && (
          <button type="button" onClick={back} data-testid="workflow-mapper-back-button" style={{ borderRadius: radius }} className="focus-ring border border-[var(--wm-border)] px-4 py-2.5 text-[12.5px] text-[var(--wm-muted)] transition-colors hover:text-[var(--wm-ink)]">
            Back
          </button>
        )}
        {step < STEPS.length - 1 ? (
          <button type="button" onClick={next} data-testid="workflow-mapper-next-button" style={{ borderRadius: radius }} className="focus-ring bg-[var(--wm-accent)] px-6 py-2.5 text-[12.5px] font-semibold text-[var(--wm-accent-ink)] transition-opacity hover:opacity-90">
            Continue
          </button>
        ) : (
          <button type="submit" disabled={status === "sending"} data-testid="workflow-mapper-submit-button" style={{ borderRadius: radius }} className="focus-ring bg-[var(--wm-accent)] px-6 py-2.5 text-[12.5px] font-semibold text-[var(--wm-accent-ink)] transition-opacity hover:opacity-90 disabled:opacity-50">
            {status === "sending" ? "Mapping…" : "Map this workflow"}
          </button>
        )}
        <span className="text-[11px] text-[var(--wm-muted)]">No deck. No discovery call marathon. A written map.</span>
      </div>
    </form>
  );
}

function Summary({ label, value, theme }) {
  return (
    <div className="border border-[var(--wm-border)] bg-[var(--wm-field)] p-4">
      <div className={`${theme.fontLabel || "f-mono"} mb-1.5 text-[9.5px] uppercase tracking-[0.14em] text-[var(--wm-muted)]`}>{label}</div>
      <div className="text-[14px] font-medium leading-snug text-[var(--wm-ink)]">{value}</div>
    </div>
  );
}
